import {Component, computed, effect, Input, input, output} from '@angular/core';
import {DatePipe} from '@angular/common';
import {MatSnackBar} from '@angular/material/snack-bar';
import {AutoUnsubscribe} from 'src/infrastructure/decorators/auto-unsubscribe.decorator';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {AdxBookingService} from '../../service/adx-booking.service';
import {PayStatusesResponse} from '../../model/pay-statuses-response';
import {MatDialog} from '@angular/material/dialog';
import {AdxBookingLinkPaymentsComponent} from '../adx-booking-link-payments-modal/adx-booking-link-payments-modal.component';
import {AzureAuthService} from 'src/infrastructure/services/azure-auth.service';
import {ConfirmationDialogCopyAdxBookingComponent} from 'src/app/adx-booking/components/popup/confirmation-dialog/confirmation-dialog-copy-adxbooking.component';
import {NewAdxBookingDialogComponent} from '../popup/new-adx-booking-dialog/new-adx-booking-dialog.component';
import {CreatePaymentConfirmationDialogComponent, PaymentConfirmationDialogResult} from '../create-payment-confirmation-dialog/create-payment-confirmation-dialog.component';
import {AdxPaymentService} from 'src/app/payments/service/adx-payment.service';
import {AdxBookingPassengersModalComponent} from 'src/app/adx-booking-passenger/components/adx-booking-passengers-modal/adx-booking-passengers-modal.component';
import moment from 'moment';
import {httpResource} from "@angular/common/http";
import {AdxBookingPassenger} from "../../../adx-booking-passenger/models/adx-booking-passenger.model";
import {ConfirmDialogData, GenericConfirmationDialogComponent} from 'src/app/shared/components/generic-confirmation-dialog/generic-confirmation-dialog.component';

@Component({
    selector: 'app-adx-booking-info',
    templateUrl: './adx-booking-info.component.html',
    standalone: false
})
@AutoUnsubscribe()
export class AdxBookingInfoComponent {
  @Input() payStatusesOptions?: PayStatusesResponse[];
  data = input<any>();
  submitedData = output<boolean>();
  confirmCopyService = output<boolean>();
  _adxBooking: any;
  adxBookingInfoForm?: FormGroup;
  isEdit = false;
  regex = /^\d{1,10}(\.\d{1,4})?$/;
  allowNegativeNumberRegex = /^-?\d{1,10}(\.\d{1,4})?$/;
  integerRegex = /^\d{1,9}$/;
  infoInvoiceField = {
    icon: 'open_in_new',
    tooltip: 'Navigate to invoice',
  };
  payStatusOptions = [
    { id: 1, description: 'Closed' },
    { id: 2, description: 'N/A' },
    { id: 3, description: 'Open' },
    { id: 4, description: 'Voided' },
  ];
  vendorPayStatusName!: string;
  clientPayStatusName!: string;
  private static readonly nullableTextFields = [
    'confirmationNumber',
    'carrier',
    'itinerary',
    'carDesc',
    'fareCode',
    'interCode',
    'commTrackNumber',
    'clientRemarks',
    'vendorRemarks',
  ];
  protected readonly loggedInUserHasDirectorOrFinanceOrManagerRole = computed(() => (this.azureAuthService.checkUserAzureTokenHasManagerRole() && this.azureAuthService.checkUserAzureTokenHasFinanceRole() && this.azureAuthService.checkUserAzureTokenHasDirectorRole()));

  private readonly bookingId = computed(() => this.data()?.id);
  private readonly getPassengersByBookingIdResource = httpResource<AdxBookingPassenger[]>(
    () => `dbov2/passenger/booking/${this.bookingId()}`
  );
  protected settlementFlags(): string {
    const booking = this._adxBooking;
    if (!booking) return '';
    const flags: string[] = [];
    if (booking.isMor) flags.push('MOR');
    if (booking.isArc) flags.push('ARC');
    if (booking.isBsp) flags.push('BSP');
    if (booking.isGross) flags.push('Gross');
    if (booking.isNet) flags.push('Net');
    return flags.length ? flags.join(', ') : 'N/A';
  }

  protected readonly passengers = computed(() => {
    const response = this.getPassengersByBookingIdResource.value();
    if (!response?.length) return 'No passengers found.';
    const firstPassenger = response[0];
    const parts = [
      firstPassenger.firstname,
      firstPassenger.middlename,
      firstPassenger.lastname
    ].filter(Boolean);
    const fullName = parts.join(' ');
    if (response.length === 1) return fullName;
    return `${fullName} +${response.length - 1} more`;
  });


  constructor(
    private datePipe: DatePipe,
    private fb: FormBuilder,
    private _snackBar: MatSnackBar,
    private adxBookingService: AdxBookingService,
    public dialog: MatDialog,
    private azureAuthService: AzureAuthService,
    private adxPaymentService: AdxPaymentService,
  ) {
    effect(() => {
      this.getAdxBookingInfo();
    });
  }

  ngOnInit() {
    this.getAdxBookingInfo();
    this.createForm();
  }

  getAdxBookingInfo() {
    if (this.data() != null) {
      this._adxBooking = this.data();
      this.vendorPayStatusName = this.payStatusesOptions?.find(o => o.id === this._adxBooking?.vendorPayStatusId)?.payStatusName || '';
      this.clientPayStatusName = this.payStatusesOptions?.find(o => o.id === this._adxBooking?.clientPayStatusId)?.payStatusName || '';
      this.resetForm();
    }
  }

  createForm(): void {
    this.adxBookingInfoForm = this.fb.group({
      commAmt: [
        this._adxBooking?.commAmt,
        [Validators.required, Validators.pattern(this.allowNegativeNumberRegex)],
      ],
      tax1Amt: [
        this._adxBooking?.tax1Amt,
        [Validators.required, Validators.pattern(this.regex)],
      ],
      tax2Amt: [
        this._adxBooking?.tax2Amt,
        [Validators.required, Validators.pattern(this.regex)],
      ],
      tax3Amt: [
        this._adxBooking?.tax3Amt,
        [Validators.required, Validators.pattern(this.regex)],
      ],
      tax4Amt: [
        this._adxBooking?.tax4Amt,
        [Validators.required, Validators.pattern(this.regex)],
      ],
      totalFare: [
        this._adxBooking?.totalFare,
        [Validators.required, Validators.pattern(this.regex)],
      ],
      totalCost: [
        this._adxBooking?.totalCost,
        [Validators.required, Validators.pattern(this.regex)],
      ],
      clientPayStatusId: [
        this._adxBooking?.clientPayStatusId,
        Validators.required,
      ],
      vendorPayStatusId: [
        this._adxBooking?.vendorPayStatusId,
        Validators.required,
      ],
      confirmationNumber: [this._adxBooking?.confirmationNumber, Validators.maxLength(1000)],
      reservationNo: [
        this._adxBooking?.reservationNo,
        Validators.pattern(this.integerRegex),
      ],
      carrier: [this._adxBooking?.carrier, Validators.maxLength(100)],
      itinerary: [this._adxBooking?.itinerary, Validators.maxLength(100)],
      carDesc: [this._adxBooking?.carDesc, Validators.maxLength(10)],
      fareCode: [this._adxBooking?.fareCode, Validators.maxLength(10)],
      interCode: [this._adxBooking?.interCode, Validators.maxLength(1)],
      commTrackNumber: [this._adxBooking?.commTrackNumber, Validators.maxLength(100)],
      clientRemarks: [this._adxBooking?.clientRemarks, Validators.maxLength(2000)],
      vendorRemarks: [this._adxBooking?.vendorRemarks, Validators.maxLength(1000)],
    });
  }

  resetForm(): void {
    this.adxBookingInfoForm?.patchValue({
      commAmt: this._adxBooking?.commAmt ?? 0,
      tax1Amt: this._adxBooking?.tax1Amt ?? 0,
      tax2Amt: this._adxBooking?.tax2Amt ?? 0,
      tax3Amt: this._adxBooking?.tax3Amt ?? 0,
      tax4Amt: this._adxBooking?.tax4Amt ?? 0,
      totalFare: this._adxBooking?.totalFare ?? 0,
      totalCost: this._adxBooking?.totalCost ?? 0,
      clientPayStatusId: this._adxBooking?.clientPayStatus?.id ?? 'NULL',
      vendorPayStatusId: this._adxBooking?.vendorPayStatus?.id ?? 'NULL',
      confirmationNumber: this._adxBooking?.confirmationNumber ?? '',
      reservationNo: this._adxBooking?.reservationNo ?? null,
      carrier: this._adxBooking?.carrier ?? '',
      itinerary: this._adxBooking?.itinerary ?? '',
      carDesc: this._adxBooking?.carDesc ?? '',
      fareCode: this._adxBooking?.fareCode ?? '',
      interCode: this._adxBooking?.interCode ?? '',
      commTrackNumber: this._adxBooking?.commTrackNumber ?? '',
      clientRemarks: this._adxBooking?.clientRemarks ?? '',
      vendorRemarks: this._adxBooking?.vendorRemarks ?? ''
    });
  }

  formatDate(date: string): string {
    return this.datePipe.transform(date, 'MM/dd/yyyy') || '';
  }

  toggleEdit(): void {
    this.isEdit = !this.isEdit;
    this.resetForm();
  }

  updateBooking(): void {
    const dialogRef = this.dialog.open(GenericConfirmationDialogComponent, {
      width: '360px',
      disableClose: true,
      data: {
        title: 'Confirm Booking Update',
        message: 'Are you sure you want to update this booking?',
        confirmLabel: 'Yes, Update',
        cancelLabel: 'Cancel',
      } as ConfirmDialogData,
    });

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return;
      this.proceedAfterBookingConfirmation();
    });
  }

  /**
   * Text inputs hand back '' once cleared and the number input hands back '' or NaN, neither of
   * which the API can bind to a nullable column - send null instead.
   */
  private normalizedFormValues(): any {
    const formValues = {...this.adxBookingInfoForm?.value};

    for (const key of AdxBookingInfoComponent.nullableTextFields) {
      const value = formValues[key];
      formValues[key] = typeof value === 'string' && value.trim() === '' ? null : value;
    }

    const reservationNo = formValues.reservationNo;
    formValues.reservationNo =
      reservationNo === '' || reservationNo === null || reservationNo === undefined || isNaN(Number(reservationNo))
        ? null
        : Number(reservationNo);

    return formValues;
  }

  private proceedAfterBookingConfirmation(): void {
    const formValues = this.normalizedFormValues();
    const previousTotalFare = this._adxBooking?.totalFare || 0;
    const newTotalFare = formValues.totalFare || 0;

    const updatedBooking = {
      ...this._adxBooking,
      ...formValues,
      submitToCode: this._adxBooking?.submitToCode[0] || '',
      adxInvoiceId: this._adxBooking?.adxInvoiceId,
      id: this._adxBooking?.id,
    };

    this.vendorPayStatusName =
      this.payStatusesOptions?.find(o => o.id === updatedBooking?.vendorPayStatusId)?.payStatusName || '';
    this.clientPayStatusName =
      this.payStatusesOptions?.find(o => o.id === updatedBooking?.clientPayStatusId)?.payStatusName || '';

    if (previousTotalFare != newTotalFare) {
      this.openPaymentConfirmationDialog(previousTotalFare, newTotalFare, updatedBooking);
    } else {
      this.proceedWithBookingUpdate(updatedBooking);
    }
  }

  private openPaymentConfirmationDialog(
    previousTotalFare: number,
    newTotalFare: number,
    updatedBooking: any
  ): void {
    const dialogRef = this.dialog.open(CreatePaymentConfirmationDialogComponent, {
      data: {
        previousTotalFare,
        newTotalFare,
        adxBookingId: this._adxBooking?.id,
        adxInvoiceId: this._adxBooking?.adxInvoiceId,
      },
      disableClose: true,
      width: '420px',
    });

    dialogRef.afterClosed().subscribe((result: PaymentConfirmationDialogResult | null) => {
      if (result == null) return;

      this.handlePaymentsAndUpdate(
        previousTotalFare,
        newTotalFare,
        updatedBooking,
        result.createVendorPayment,
        result.createClientPayment
      );
    });
  }

  private handlePaymentsAndUpdate(
    previousTotalFare: number,
    newTotalFare: number,
    updatedBooking: any,
    createVendor: boolean,
    createClient: boolean
  ): void {
    const fareChange = newTotalFare - previousTotalFare;

    const execute = (paymentData: any | null, label: string, next: () => void) => {
      if (!paymentData) { next(); return; }
      this.adxPaymentService.createAdxPayment(paymentData).subscribe({
        next: () => {
          this._snackBar.open(`${label} payment created successfully`, 'Dismiss', {
            horizontalPosition: 'center', verticalPosition: 'bottom', duration: 3000,
          });
          this.adxPaymentService.triggerAdxPaymentGridRefresh();
          next();
        },
        error: () => {
          this._snackBar.open(`Failed to create ${label} payment. Proceeding anyway.`, 'Dismiss', {
            horizontalPosition: 'center', verticalPosition: 'bottom', duration: 3000,
          });
          next();
        }
      });
    };

    execute(
      createVendor ? this.buildVendorPaymentData(fareChange, previousTotalFare, newTotalFare) : null,
      'Vendor',
      () => execute(
        createClient ? this.buildClientPaymentData(fareChange, previousTotalFare, newTotalFare) : null,
        'Client',
        () => this.proceedWithBookingUpdate(updatedBooking)
      )
    );
  }

  private buildVendorPaymentData(fareChange: number, previousTotalFare: number, newTotalFare: number): any {
    return {
      adxBookingId: this._adxBooking?.id,
      adxInvoiceId: this._adxBooking?.adxInvoiceId,
      amount: fareChange,
      paymentDate: moment().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      remarks: `Fare adjustment for service ${this._adxBooking?.id}. Previous fare: ${previousTotalFare}, New fare: ${newTotalFare}`,
      payeeName: this._adxBooking?.vendor?.vendorName || 'Unknown Vendor',
      currencyId: this._adxBooking?.currencyId || 1,
      dataSourceId: this._adxBooking?.dataSourceId,
      payTypeId: 2,
      payMethodId: 3,
      profileTypeCode: 'V'
    };
  }

  private buildClientPaymentData(fareChange: number, previousTotalFare: number, newTotalFare: number): any {
    return {
      adxBookingId: this._adxBooking?.id,
      adxInvoiceId: this._adxBooking?.adxInvoiceId,
      amount: fareChange,
      paymentDate: moment().format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'),
      remarks: `Fare adjustment for service ${this._adxBooking?.id}. Previous fare: ${previousTotalFare}, New fare: ${newTotalFare}`,
      payeeName: this._adxBooking?.vendor?.vendorName || 'Unknown Vendor',
      currencyId: this._adxBooking?.currencyId || 1,
      dataSourceId: this._adxBooking?.dataSourceId,
      payTypeId: 1,
      payMethodId: 3,
      profileTypeCode: 'I'
    };
  }

  private proceedWithBookingUpdate(updatedBooking: any): void {
    this.adxBookingService.updateAdxBooking(updatedBooking).subscribe(() => {
      this.isEdit = false;
      this.submitedData.emit(true);

      this._snackBar.open('Booking updated successfully', 'Dismiss', {
        horizontalPosition: 'center',
        verticalPosition: 'bottom',
        duration: 3000,
      });
    });
  }

  hasRedirect(): boolean {
    return this._adxBooking?.adxInvoiceId && !this._adxBooking.isModal;
  }

  redirectToInvoice(): void {
    if (this.hasRedirect()) {
      location.href = `AdxInvoices/${this._adxBooking?.adxInvoiceId}`;
    }
  }

  openPaymentsLinkModal(event: MouseEvent, paymentType: string): void {
    event.preventDefault();
    this.dialog.closeAll();

    const profileTypeId = paymentType === 'client' ? 3 : paymentType === 'vendor' ? 5 : 0;

    this.dialog.open(AdxBookingLinkPaymentsComponent, {
      data: {
        bookingId: this._adxBooking?.id,
        profileTypeId: profileTypeId
      }
    });
  }

  openPassengersModal(event?: MouseEvent): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.dialog.open(AdxBookingPassengersModalComponent, {
      width: '50%',
      data: {
        bookingId: this._adxBooking?.id,
        bookingNo: this._adxBooking?.id?.toString() || ''
      }
    });
  }

  openCopyConfirmationDialog(adxBookingId : number): void {
    const dialogRef = this.dialog.open(ConfirmationDialogCopyAdxBookingComponent, {
      data: {
        adxBookingId: adxBookingId
      },
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result){
        if(this._adxBooking.isModal){
          this.confirmCopyService.emit(true);
          dialogRef.close(true);
        }
        else{
          this.openNewAdxBookingConfirmationDialog(adxBookingId, result);
        }
      }
    });
  }

  openNewAdxBookingConfirmationDialog(currentAdxBookingId : number, newAdxBookingId: number): void {
    this.dialog.open(NewAdxBookingDialogComponent, {
      data: {
        currentAdxBookingId: currentAdxBookingId,
        newAdxBookingId: newAdxBookingId
      },
    });
  }
}
